import {
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import Close from "@mui/icons-material/Close";
import FilterList from "@mui/icons-material/FilterList";
import Info from "@mui/icons-material/Info";
import Warning from "@mui/icons-material/Warning";
import {
  Box,
  ButtonBase,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import { dayjs, NhsNumberSchema } from "@thalamos/common";
import type {
  AvailablePatientListRow,
  PatientListEntry,
  PatientListSortBy,
  PatientListSortDirection,
  PatientStateWithContext,
} from "@thalamos/aspire-common";
import {
  defaultPatientListSort,
  getActiveMhaStatus,
  MhaStatus,
} from "@thalamos/aspire-common";

import { FilterableColumnHeader } from "./FilterableColumnHeader.js";
import type { FilterOption } from "./FilterableColumnHeader.js";
import { FilterAndSortDialog } from "./FilterAndSortDialog.js";
import { PatientSearchField } from "./PatientSearchField.js";
import {
  SortableColumnHeader,
  type PatientListSort,
} from "./SortableColumnHeader.js";
import {
  Banner,
  BannerList,
  Button,
  ButtonIcon,
  LoadingSpinner,
  type ThalamosButtonProps,
} from "~/components/design-system/index.js";
import { TooltipInfoBox } from "~/components/design-system/TooltipInfoBox.js";
import { formatMhaStatus } from "~/components/util.js";
import PaginationControls from "~/pages/Caseload/PaginationControls.js";
import { ConfirmationModal } from "~/pages/ConfirmationModal.js";
import {
  classifyExpiry,
  expiryThresholdTimes,
  hospitalOrPosName,
} from "~/pages/MhaStatusDashboard/MhaStatusDashboard.js";
import { routeFns } from "~/routePaths.js";
import { ukLocalFormatDate } from "~/util.js";

import "../../@types/index.js";

const COLUMN_COUNT = 8;

// About the narrowest the table goes before Add patient, in its header, is
// pushed out of view; narrower than this the list is shown as cards instead.
const tableMinWidthPx = 900;

/**
 * Whether the element the returned ref is attached to is narrower than
 * `widthPx`, kept current as it resizes. It measures an element, not the
 * viewport, because the menu drawer beside the page may be open or closed.
 * Attach the ref on the first render: an element attached later is never
 * observed.
 */
function useIsNarrowerThan<T extends HTMLElement>(
  widthPx: number,
): [RefObject<T | null>, boolean] {
  const ref = useRef<T>(null);
  const [isNarrower, setIsNarrower] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (element === null) return;
    const measure = () => {
      setIsNarrower(element.clientWidth < widthPx);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [widthPx]);

  return [ref, isNarrower];
}

export type PatientListProps = {
  entries: PatientListEntry[];
  total: number;
  page: number;
  rowsPerPage: number;
  isLoading: boolean;
  error?: string;
  sort: PatientListSort;
  onSortChange: (sort: PatientListSort) => void;
  searchTerm: string;
  onSearchChange: (searchTerm: string) => void;
  onClearFilters: () => void;
  availableMhaStatuses: MhaStatus[];
  selectedMhaStatuses: MhaStatus[];
  onMhaStatusesChange: (statuses: MhaStatus[]) => void;
  availableLocationNames: string[];
  selectedLocationNames: string[];
  onLocationNamesChange: (locationNames: string[]) => void;
  onPageChange: (page: number) => void;
  onAddPatient?: () => void;
  onRemovePatient: (rootPatientId: string) => Promisable<void>;
};

// The list writes every date with slashes, including those that carry a time.
function formatListDateTime(utcValue: string) {
  return dayjs.utc(utcValue).tz("Europe/London").format("DD/MM/YYYY HH:mm");
}

type RemovalControl = {
  isRemoving: boolean;
  onRemove: () => Promisable<void>;
};

function RemoveFromListButton({
  patientName,
  isRemoving,
  onRemove,
}: RemovalControl & { patientName: string | null }) {
  return (
    <IconButton
      size="small"
      aria-label={
        patientName === null
          ? "Remove this patient from your patient list"
          : `Remove ${patientName} from your patient list`
      }
      disabled={isRemoving}
      onClick={() => {
        void onRemove();
      }}
    >
      <Close fontSize="small" />
    </IconButton>
  );
}

function ExpiryValue({ state }: { state: PatientStateWithContext }) {
  const theme = useTheme();
  const activeState = getActiveMhaStatus(state.state, dayjs().toISOString());
  const status = state.state.mhaStatus.status;
  const expiryDateTime = state.state.mhaStatus.expiryDateTime;

  const classification = classifyExpiry(
    status,
    expiryDateTime,
    dayjs(),
    activeState.type,
  );

  if (classification === "unknown" || !expiryDateTime) {
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Warning sx={{ color: "orange", marginRight: "0.2em" }} />
        Unknown
      </Box>
    );
  }

  const formatExpiry = expiryThresholdTimes[status]?.showTime
    ? formatListDateTime
    : ukLocalFormatDate;
  const formatted = formatExpiry(expiryDateTime);

  const overridden = state.expiryOverrideInfo;
  const dateWithOverrideHint = (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {formatted}
      {overridden && (
        <TooltipInfoBox
          title={`Expiry time manually overridden from ${formatExpiry(overridden.defaultExpiryDateTime)}`}
          icon={
            <Info
              sx={{
                color: theme.palette.primary.main,
                fontSize: "1.25rem",
                ml: 0.5,
              }}
            />
          }
        />
      )}
    </Box>
  );

  const warningColour: Record<string, string | undefined> = {
    expired: "red",
    critical: "red",
    warning: "orange",
    normal: undefined,
  };
  const colour = warningColour[classification];

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {colour && <Warning sx={{ color: colour, marginRight: "0.2em" }} />}
      {classification === "expired" ? (
        <Box>Expired ({formatted})</Box>
      ) : (
        dateWithOverrideHint
      )}
    </Box>
  );
}

// The substantive detention leads; a Section 136 sits under it.
function detentionsInDisplayOrder(row: AvailablePatientListRow) {
  return [...row.states].sort((a, b) => {
    const a136 = a.state.mhaStatus.status === MhaStatus.Section136 ? 1 : 0;
    const b136 = b.state.mhaStatus.status === MhaStatus.Section136 ? 1 : 0;
    return a136 - b136;
  });
}

function mhaStatusText(state: PatientStateWithContext) {
  const activeState = getActiveMhaStatus(state.state, dayjs().toISOString());
  return formatMhaStatus(activeState.state, activeState.type);
}

function dateOfBirthText(row: AvailablePatientListRow) {
  return row.patientInfo.dateOfBirth
    ? ukLocalFormatDate(row.patientInfo.dateOfBirth)
    : "Unknown";
}

function nhsNumberText(row: AvailablePatientListRow) {
  return NhsNumberSchema.catch("Unknown").parse(row.patientInfo.nhsNumber);
}

function givenThenFamilyName(row: AvailablePatientListRow) {
  return `${row.patientInfo.name.given} ${row.patientInfo.name.family}`;
}

function PatientNameLink({ row }: { row: AvailablePatientListRow }) {
  const isDeceasedPatient = ["formal", "informal"].includes(
    row.patientInfo.deathNotificationStatus ?? "",
  );

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Link
        style={{ textDecoration: "none" }}
        to={routeFns.patientHome(row.rootPatientId)}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontSize: "0.9375rem", color: "blue" }}
        >
          {`${row.patientInfo.name.family.toUpperCase()}, ${row.patientInfo.name.given}`}
        </Typography>
      </Link>
      {isDeceasedPatient && (
        <TooltipInfoBox
          title="This patient has been marked as deceased in NHS national systems"
          icon={<Info sx={{ fontSize: "1.5rem", ml: 1 }} />}
        />
      )}
    </Box>
  );
}

function UnavailablePatientName() {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      Patient unavailable
      <TooltipInfoBox
        title="You no longer have access to this patient. They remain on your list in case access is restored."
        icon={<Info sx={{ fontSize: "1.25rem", ml: 1 }} />}
      />
    </Box>
  );
}

function ViewPatientButton({
  rootPatientId,
  fullWidth,
}: {
  rootPatientId: string;
  fullWidth?: boolean;
}) {
  const navigate = useNavigate();

  // A link may not contain a button, so View navigates rather than wrapping
  // in a Link.
  return (
    <Button
      label="View"
      fullWidth={fullWidth}
      onClick={() => {
        navigate(routeFns.patientHome(rootPatientId));
      }}
    />
  );
}

const sortDescriptions: Record<
  PatientListSortBy,
  Record<PatientListSortDirection, string>
> = {
  recentActivity: { desc: "recently added", asc: "earliest added" },
  name: { asc: "name A–Z", desc: "name Z–A" },
  dateOfBirth: {
    asc: "date of birth, oldest first",
    desc: "date of birth, youngest first",
  },
  expiry: { asc: "expiry, soonest first", desc: "expiry, latest first" },
};

function CountBadge({ count }: { count: number }) {
  const theme = useTheme();

  return (
    <Box
      component="span"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: "1.25rem",
        height: "1.25rem",
        borderRadius: "50%",
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        fontWeight: 700,
      }}
    >
      {count}
    </Box>
  );
}

function AddPatientButton({
  onAddPatient,
  fullWidth,
  size,
}: Pick<PatientListProps, "onAddPatient"> &
  Pick<ThalamosButtonProps, "fullWidth" | "size">) {
  return (
    <Button
      label="Add patient"
      endIcon={ButtonIcon.add}
      outlined
      fullWidth={fullWidth}
      size={size}
      sx={{ whiteSpace: "nowrap" }}
      onClick={onAddPatient}
      testId="add-patient"
    />
  );
}

export function PatientListRow({
  row,
  index,
  isRemoving,
  onRemove,
}: RemovalControl & {
  row: AvailablePatientListRow;
  index: number;
}) {
  const theme = useTheme();

  const states = detentionsInDisplayOrder(row);
  const noStatus = states.length === 0 ? "—" : null;

  const stackSx = { display: "flex", flexDirection: "column", gap: 1 } as const;

  return (
    <TableRow
      sx={{
        backgroundColor:
          index % 2 === 0 ? theme.palette.secondary.light : "white",
      }}
    >
      <TableCell padding="checkbox">
        <RemoveFromListButton
          patientName={givenThenFamilyName(row)}
          isRemoving={isRemoving}
          onRemove={onRemove}
        />
      </TableCell>
      <TableCell>
        <PatientNameLink row={row} />
      </TableCell>
      <TableCell>{dateOfBirthText(row)}</TableCell>
      <TableCell>{nhsNumberText(row)}</TableCell>
      <TableCell>
        <Box sx={stackSx}>
          {noStatus}
          {states.map((s) => (
            <Box key={s.id}>{hospitalOrPosName(s)}</Box>
          ))}
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={stackSx}>
          {noStatus}
          {states.map((s) => (
            <Box key={s.id}>{mhaStatusText(s)}</Box>
          ))}
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={stackSx}>
          {noStatus}
          {states.map((s) => (
            <Box key={s.id}>
              <ExpiryValue state={s} />
            </Box>
          ))}
        </Box>
      </TableCell>
      <TableCell align="right">
        <ViewPatientButton rootPatientId={row.rootPatientId} />
      </TableCell>
    </TableRow>
  );
}

export function UnavailablePatientListRow({
  index,
  isRemoving,
  onRemove,
}: RemovalControl & { index: number }) {
  const theme = useTheme();
  const disabledSx = { color: theme.palette.text.disabled };

  return (
    <TableRow
      sx={{
        backgroundColor:
          index % 2 === 0 ? theme.palette.secondary.light : "white",
        ...disabledSx,
      }}
    >
      <TableCell padding="checkbox">
        <RemoveFromListButton
          patientName={null}
          isRemoving={isRemoving}
          onRemove={onRemove}
        />
      </TableCell>
      <TableCell sx={disabledSx}>
        <UnavailablePatientName />
      </TableCell>
      <TableCell sx={disabledSx}>{"—"}</TableCell>
      <TableCell sx={disabledSx}>{"—"}</TableCell>
      <TableCell sx={disabledSx}>{"—"}</TableCell>
      <TableCell sx={disabledSx}>{"—"}</TableCell>
      <TableCell sx={disabledSx}>{"—"}</TableCell>
      <TableCell />
    </TableRow>
  );
}

function CardField({
  label,
  spansBothColumns,
  children,
}: {
  label: string;
  spansBothColumns?: boolean;
  children: ReactNode;
}) {
  const theme = useTheme();

  return (
    <Box sx={spansBothColumns ? { gridColumn: "1 / -1" } : undefined}>
      <Box
        component="dt"
        sx={{
          fontSize: "0.75rem",
          fontWeight: 700,
          color: theme.palette.text.secondary,
        }}
      >
        {label}
      </Box>
      <Box component="dd" sx={{ margin: 0 }}>
        {children}
      </Box>
    </Box>
  );
}

function CardFields({
  hasDivider,
  children,
}: {
  hasDivider?: boolean;
  children: ReactNode;
}) {
  const theme = useTheme();

  return (
    <Box
      component="dl"
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "0.75em 1em",
        margin: 0,
        ...(hasDivider && {
          borderTop: `1px solid ${theme.palette.text.border}`,
          paddingTop: "0.75em",
        }),
      }}
    >
      {children}
    </Box>
  );
}

function PatientCard({
  heading,
  removeButton,
  children,
}: {
  heading: ReactNode;
  removeButton: ReactNode;
  children?: ReactNode;
}) {
  const theme = useTheme();

  return (
    <Paper
      component="li"
      elevation={0}
      sx={{
        typography: "body2",
        display: "flex",
        flexDirection: "column",
        gap: "0.75em",
        padding: "0.75em 1em 1em",
        border: `1px solid ${theme.palette.common.lightGray}`,
        borderRadius: "6px",
        boxShadow: "0 1px 4px rgba(11, 18, 44, 0.08)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.5em",
        }}
      >
        {heading}
        {removeButton}
      </Box>
      {children}
    </Paper>
  );
}

export function PatientListCard({
  row,
  isRemoving,
  onRemove,
}: RemovalControl & { row: AvailablePatientListRow }) {
  const states = detentionsInDisplayOrder(row);

  return (
    <PatientCard
      heading={<PatientNameLink row={row} />}
      removeButton={
        <RemoveFromListButton
          patientName={givenThenFamilyName(row)}
          isRemoving={isRemoving}
          onRemove={onRemove}
        />
      }
    >
      <CardFields>
        <CardField label="DOB">{dateOfBirthText(row)}</CardField>
        <CardField label="NHS Number">{nhsNumberText(row)}</CardField>
      </CardFields>
      {states.length === 0 ? (
        <CardFields hasDivider>
          <CardField label="MHA Status">{"—"}</CardField>
        </CardFields>
      ) : (
        states.map((s) => (
          <CardFields key={s.id} hasDivider>
            <CardField label="MHA Status">{mhaStatusText(s)}</CardField>
            <CardField label="Expiry">
              <ExpiryValue state={s} />
            </CardField>
            <CardField label="Hospital / Place of Safety" spansBothColumns>
              {hospitalOrPosName(s)}
            </CardField>
          </CardFields>
        ))
      )}
      <ViewPatientButton rootPatientId={row.rootPatientId} fullWidth />
    </PatientCard>
  );
}

export function UnavailablePatientListCard({
  isRemoving,
  onRemove,
}: RemovalControl) {
  const theme = useTheme();

  return (
    <PatientCard
      heading={
        <Box sx={{ color: theme.palette.text.disabled }}>
          <UnavailablePatientName />
        </Box>
      }
      removeButton={
        <RemoveFromListButton
          patientName={null}
          isRemoving={isRemoving}
          onRemove={onRemove}
        />
      }
    />
  );
}

export function PatientList({
  entries,
  total,
  page,
  rowsPerPage,
  isLoading,
  error,
  sort,
  onSortChange,
  searchTerm,
  onSearchChange,
  onClearFilters,
  availableMhaStatuses,
  selectedMhaStatuses,
  onMhaStatusesChange,
  availableLocationNames,
  selectedLocationNames,
  onLocationNamesChange,
  onPageChange,
  onAddPatient,
  onRemovePatient,
}: PatientListProps) {
  const theme = useTheme();
  const [widthRef, showsCards] =
    useIsNarrowerThan<HTMLDivElement>(tableMinWidthPx);
  const [confirmingRemovalOf, setConfirmingRemovalOf] = useState<string | null>(
    null,
  );
  const [removing, setRemoving] = useState<ReadonlySet<string>>(new Set());
  const [isChoosingFilterAndSort, setIsChoosingFilterAndSort] = useState(false);

  const removes = async (rootPatientId: string) => {
    setConfirmingRemovalOf(null);
    setRemoving((current) => new Set([...current, rootPatientId]));
    try {
      await onRemovePatient(rootPatientId);
    } finally {
      setRemoving(
        (current) =>
          new Set([...current].filter((pending) => pending !== rootPatientId)),
      );
    }
  };

  const removalControlFor = (rootPatientId: string): RemovalControl => ({
    isRemoving: removing.has(rootPatientId),
    onRemove: () => {
      setConfirmingRemovalOf(rootPatientId);
    },
  });

  const isSorted =
    sort.sortBy !== defaultPatientListSort.sortBy ||
    sort.sortDirection !== defaultPatientListSort.sortDirection;
  const isFiltered =
    selectedMhaStatuses.length > 0 || selectedLocationNames.length > 0;

  const stillSelectableWhenNoLongerOffered = <T extends string>(
    available: T[],
    chosen: T[],
  ): T[] => [...available, ...chosen.filter((one) => !available.includes(one))];

  const mhaStatusLabel = (status: MhaStatus) =>
    formatMhaStatus(status, "active");

  const mhaStatusOptions = stillSelectableWhenNoLongerOffered(
    availableMhaStatuses,
    selectedMhaStatuses,
  )
    .map((status) => ({ value: status, label: mhaStatusLabel(status) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const locationOptions: FilterOption[] = stillSelectableWhenNoLongerOffered(
    availableLocationNames,
    selectedLocationNames,
  ).map((name) => ({ value: name, label: name }));

  // onClick makes the whole pill clear; onDelete adds the cross, whose click
  // MUI keeps from also firing onClick.
  const clearFiltersChip = (isSorted || isFiltered) && (
    <Chip
      label="Clear filters"
      color="primary"
      variant="outlined"
      deleteIcon={<Close />}
      onClick={onClearFilters}
      onDelete={onClearFilters}
      data-testid="clear-filters"
      sx={{ fontWeight: 700 }}
    />
  );

  const showsTable = !showsCards && error === undefined && !isLoading;

  const filterPills = [
    ...selectedLocationNames.map((name) => ({
      key: `location:${name}`,
      label: name,
      onRemove: () => {
        onLocationNamesChange(
          selectedLocationNames.filter((one) => one !== name),
        );
      },
    })),
    ...selectedMhaStatuses.map((status) => ({
      key: `mhaStatus:${status}`,
      label: mhaStatusLabel(status),
      onRemove: () => {
        onMhaStatusesChange(
          selectedMhaStatuses.filter((one) => one !== status),
        );
      },
    })),
  ];

  // Without the table's header to hold them, the card layout gathers the sort
  // and filters into one dialog, and shows what is applied above the cards.
  // They stay there while the list loads, so they do not vanish each time a
  // change to them reloads the list.
  const toolbar = showsCards ? (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "0.75em",
        marginBottom: "1em",
      }}
    >
      <PatientSearchField value={searchTerm} onChange={onSearchChange} />
      <Box
        sx={{
          display: "grid",
          // Side by side only where half the row fits Filter & sort's label,
          // icon and count on one line; stacked on narrower phones.
          gridTemplateColumns: "repeat(auto-fit, minmax(164px, 1fr))",
          gap: "0.75em",
        }}
      >
        <Button
          label="Filter & sort"
          outlined
          fullWidth
          size="small"
          customStartIcon={<FilterList />}
          customEndIcon={
            filterPills.length > 0 ? (
              <CountBadge count={filterPills.length} />
            ) : undefined
          }
          // Tighter than the design system's spacing so that the label, icon and
          // count fit half the width of a phone; the doubled class outranks the
          // padding the Button sets. MUI sizes an end icon's first child as an
          // icon, but the count is text.
          sx={{
            whiteSpace: "nowrap",
            "&&": { paddingX: 1.5 },
            "& .MuiButton-startIcon": { marginRight: 0.5 },
            "& .MuiButton-endIcon": { marginLeft: 0.5 },
            "& .MuiButton-endIcon > *": { fontSize: "0.625rem" },
          }}
          onClick={() => {
            setIsChoosingFilterAndSort(true);
          }}
        />
        <AddPatientButton onAddPatient={onAddPatient} fullWidth size="small" />
      </Box>
      {(isSorted || isFiltered) && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "0.5em 0.75em",
          }}
        >
          <Typography sx={{ fontSize: "0.875rem" }}>
            Sorted by {sortDescriptions[sort.sortBy][sort.sortDirection]}
          </Typography>
          {filterPills.map((pill) => (
            <Chip
              key={pill.key}
              label={pill.label}
              aria-label={`Remove the ${pill.label} filter`}
              color="primary"
              variant="outlined"
              deleteIcon={<Close />}
              onClick={pill.onRemove}
              onDelete={pill.onRemove}
              sx={{ fontWeight: 700 }}
            />
          ))}
          <ButtonBase
            onClick={onClearFilters}
            sx={{
              color: theme.palette.primary.main,
              fontSize: "0.875rem",
              fontWeight: 700,
              textDecoration: "underline",
              borderRadius: "4px",
              padding: "0.1em 0.2em",
            }}
          >
            Clear all
          </ButtonBase>
        </Box>
      )}
      {isChoosingFilterAndSort && (
        <FilterAndSortDialog
          sort={sort}
          onSortChange={onSortChange}
          locationOptions={locationOptions}
          selectedLocationNames={selectedLocationNames}
          onLocationNamesChange={onLocationNamesChange}
          mhaStatusOptions={mhaStatusOptions}
          selectedMhaStatuses={selectedMhaStatuses}
          onMhaStatusesChange={onMhaStatusesChange}
          onClose={() => {
            setIsChoosingFilterAndSort(false);
          }}
        />
      )}
    </Box>
  ) : (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "1em",
        marginBottom: "1em",
      }}
    >
      <PatientSearchField value={searchTerm} onChange={onSearchChange} />
      {!showsTable && clearFiltersChip}
    </Box>
  );

  const firstOnPage = total === 0 ? 0 : page * rowsPerPage + 1;
  const lastOnPage = Math.min(total, page * rowsPerPage + entries.length);

  const emptyMessage =
    searchTerm.trim() === "" && !isFiltered
      ? "Your patient list is empty. Add to this by clicking 'Add Patient +'."
      : "No patients on your list match what you are looking for";

  const cards =
    entries.length === 0 ? (
      <Typography sx={{ padding: "1em 0" }}>{emptyMessage}</Typography>
    ) : (
      <Box
        component="ul"
        aria-label="Patient list"
        sx={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          display: "flex",
          flexDirection: "column",
          gap: "0.75em",
        }}
      >
        {entries.map((entry) =>
          match(entry)
            .with({ kind: "available" }, ({ row }) => (
              <PatientListCard
                key={row.rootPatientId}
                row={row}
                {...removalControlFor(row.rootPatientId)}
              />
            ))
            .with({ kind: "unavailable" }, ({ rootPatientId }) => (
              <UnavailablePatientListCard
                key={rootPatientId}
                {...removalControlFor(rootPatientId)}
              />
            ))
            .exhaustive(),
        )}
      </Box>
    );

  const tableBody =
    entries.length === 0 ? (
      <TableRow>
        <TableCell colSpan={COLUMN_COUNT}>
          <Typography sx={{ padding: "2.5em 0.5em" }}>
            {emptyMessage}
          </Typography>
        </TableCell>
      </TableRow>
    ) : (
      entries.map((entry, index) =>
        match(entry)
          .with({ kind: "available" }, ({ row }) => (
            <PatientListRow
              key={row.rootPatientId}
              row={row}
              index={index}
              {...removalControlFor(row.rootPatientId)}
            />
          ))
          .with({ kind: "unavailable" }, ({ rootPatientId }) => (
            <UnavailablePatientListRow
              key={rootPatientId}
              index={index}
              {...removalControlFor(rootPatientId)}
            />
          ))
          .exhaustive(),
      )
    );

  const table = (
    <TableContainer component={Paper}>
      <Table
        size="small"
        sx={{
          "& .MuiTableCell-root:not(.MuiTableCell-paddingCheckbox)": {
            paddingX: 1.5,
          },
        }}
      >
        <TableHead
          sx={{
            "& .MuiTableCell-head": {
              paddingY: 1.5,
              borderBottom: `2px solid ${theme.palette.text.border}`,
            },
          }}
        >
          {clearFiltersChip && (
            <TableRow>
              {/* A row of its own lines the pill up with the patients' names.
                  Its cells are plain cells, not header cells, so the pill is
                  never a heading for the columns beneath it. */}
              <TableCell
                component="td"
                variant="body"
                padding="checkbox"
                sx={{ borderBottom: "none" }}
              />
              <TableCell
                component="td"
                variant="body"
                colSpan={COLUMN_COUNT - 1}
                sx={{ borderBottom: "none", paddingTop: 1.5, paddingBottom: 0 }}
              >
                {clearFiltersChip}
              </TableCell>
            </TableRow>
          )}
          <TableRow>
            <TableCell padding="checkbox" />
            <SortableColumnHeader
              label="Name"
              column="name"
              options={[
                { direction: "asc", label: "A → Z", badge: "A→Z" },
                { direction: "desc", label: "Z → A", badge: "Z→A" },
              ]}
              sort={sort}
              onSortChange={onSortChange}
            />
            <SortableColumnHeader
              label="DOB"
              column="dateOfBirth"
              options={[
                { direction: "asc", label: "Oldest first", badge: "Oldest" },
                {
                  direction: "desc",
                  label: "Youngest first",
                  badge: "Youngest",
                },
              ]}
              sort={sort}
              onSortChange={onSortChange}
            />
            <TableCell sx={{ fontWeight: 700 }}>NHS Number</TableCell>
            <FilterableColumnHeader
              label="Hospital / Place of Safety"
              options={locationOptions}
              selected={selectedLocationNames}
              onSelectionChange={onLocationNamesChange}
            />
            <FilterableColumnHeader
              label="MHA Status"
              options={mhaStatusOptions}
              selected={selectedMhaStatuses}
              onSelectionChange={onMhaStatusesChange}
            />
            <SortableColumnHeader
              label="Expiry"
              column="expiry"
              options={[
                {
                  direction: "asc",
                  label: "Soonest first",
                  badge: "Soonest",
                },
                { direction: "desc", label: "Latest first", badge: "Latest" },
              ]}
              sort={sort}
              onSortChange={onSortChange}
            />
            <TableCell align="right">
              <AddPatientButton onAddPatient={onAddPatient} />
            </TableCell>
          </TableRow>
        </TableHead>
        {/* Scoped to the body so it misses the body cells of the Clear
            filters row in the head. */}
        <TableBody sx={{ "& .MuiTableCell-body": { paddingY: 1.5 } }}>
          {tableBody}
        </TableBody>
      </Table>
    </TableContainer>
  );

  let content: ReactNode;
  if (error !== undefined) {
    content = <Banner bannerType={BannerList.ERROR} title={error} />;
  } else if (isLoading) {
    content = <LoadingSpinner />;
  } else {
    content = (
      <>
        {confirmingRemovalOf !== null && (
          <ConfirmationModal
            title="Confirm you want to remove the patient from your list?"
            message="You can add them again using the 'Add Patient +' button."
            confirmFn={() => {
              void removes(confirmingRemovalOf);
            }}
            closeFn={() => {
              setConfirmingRemovalOf(null);
            }}
          />
        )}
        {showsCards ? cards : table}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            marginTop: "1em",
          }}
        >
          <Typography sx={{ fontSize: "0.875rem" }}>
            {total === 0
              ? "No patients"
              : `Showing ${String(firstOnPage)}–${String(lastOnPage)} of ${String(total)} patient${total === 1 ? "" : "s"}`}
          </Typography>
          <PaginationControls
            numPages={Math.max(1, Math.ceil(total / rowsPerPage))}
            page={page}
            setPage={onPageChange}
          />
        </Box>
      </>
    );
  }

  return (
    <Box ref={widthRef}>
      {toolbar}
      {content}
    </Box>
  );
}
